import { useMemo } from 'react';
import {
  Datagrid,
  Record,
  TextField,
  BooleanField,
  ReferenceField,
  DateField,
  NumberField,
  FunctionField,
  useListContext,
  Loading
} from 'react-admin';

import { uniqueId } from 'lodash';
import { EditConsentTagButton } from 'resources/contents/EditConsentTagButton.component';
import { EditMarketplacePublicButton } from 'resources/contents/EditMarketplacePublicButton.component';

import { FieldObject } from 'resources/types';
import { BPErrorView } from 'shared/components/common/BPErrorView.component';
import { LinkableField } from 'shared/components/fields/LinkableField.component';
import { ResourceName } from 'shared/enums';
import { useAppPermissions } from 'shared/hooks/useAppPermissions';
import { HasOmitItems } from 'shared/interfaces/react-admin';
import { isPublisher } from 'shared/services/firebase-auth-provider/checkPermissions';
import { renderFullName } from 'shared/utils/helperFunctions/renderFullName';
import { shortenString } from 'shared/utils/helperFunctions/shortenString';

import { v4 } from 'uuid';

import { ContentItem } from '../../activities/models/ContentItem.model';

export const UserActivitiesTable = (
  props: Partial<HasOmitItems<keyof ContentItem>>
) => {
  const listContext = useListContext<ContentItem>();
  const { permissions } = useAppPermissions();
  
  // Define basic data fields
  const basicFields: Array<FieldObject<keyof ContentItem>> = [
    {
      source: 'display_name',
      element: (
        <LinkableField
          source={'display_name'}
          label='Activity Name'
          render={(record?: Record) =>
            shortenString(record?.display_name ?? '', 50)
          }
          emptyText='-'
          key={v4()}
        />
      )
    },
    {
      source: 'creator_id',
      element: (
        <ReferenceField
          reference={ResourceName.USERS}
          label='User email'
          source='creator_id'
          link={'show'}
          key={uniqueId()}
          sortBy='email'
        >
          <TextField source='email' />
        </ReferenceField>
      )
    },
    {
      source: 'creator_id',
      element: (
        <ReferenceField
          source='creator_id'
          reference={ResourceName.USERS}
          link={false}
          sortable={false}
          label='Name'
          emptyText='-'
          key={v4()}
        >
          <FunctionField render={renderFullName} />
        </ReferenceField>
      )
    },
    {
      source: 'updated_at',
      element: (
        <DateField
          source='updated_at'
          label='Last changed date'
          emptyText='-'
          key={v4()}
        />
      )
    },
    {
      source: 'plays',
      element: (
        <NumberField source='plays' label='Times played' emptyText='-' key={v4()} />
      )
    },
    {
      source: 'has_video',
      element: (
        <BooleanField
          sortable={false}
          source='has_video'
          label='Video'
          emptyText='-'
          key={v4()}
        />
      )
    }
  ];
  
  // Define action button fields
  const actionFields: Array<FieldObject<keyof ContentItem>> = isPublisher(permissions?.content) 
    ? [
        {
          source: 'consent' as keyof ContentItem,
          element: (
            <EditConsentTagButton key="consent-button" />
          )
        },
        {
          source: 'published' as keyof ContentItem,
          element: (
            <EditMarketplacePublicButton key="marketplace-button" />
          )
        }
      ]
    : [];
  
  // Combine all fields
  const allFields = [...basicFields, ...actionFields];
  
  // Use memoization for filtering fields based on omitItems
  const fields = useMemo(() => {
    const output: JSX.Element[] = [];

    for (const field of allFields) {
      if (props.omitItems ? !props.omitItems.includes(field.source) : true) {
        output.push(field.element);
      }
    }
    return output;
  }, [props.omitItems, allFields]);

  return (
    <>
      {listContext.loading ? (
        <Loading />
      ) : listContext.error ? (
        <BPErrorView error={listContext.error} />
      ) : (
        <Datagrid>
          {fields}
        </Datagrid>
      )}
    </>
  );
};
